"use client";

import { useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import FadeIn from "../ui/FadeIn";
import TiltCard from "../ui/TiltCard";
import UniqueCapabilities from "./UniqueCapabilities";
import { cdnUrl } from "@/lib/cdn";

// NOTE: the `?v=2` on these three image URLs is a deliberate cache-buster, not
// a stray param. The image proxy (images.hsingh.app / Caddy) had cached a
// truncated 206 partial response for the 384w variant of each and served it
// with a 1-year max-age, so browsers that picked that srcset candidate got a
// blank image. Bumping the query changes the proxy's cache key and forces a
// clean re-fetch across every width. S3 ignores the unknown param. If you
// re-upload any of these, drop the `?v=` — a fresh filename is its own key.
// Cause, purge command and the origin-side fix: docs/image-proxy-206-cache.md.
const features = [
  {
    title: "Error-Free Numbers",
    href: "/products#co-analyst",
    description:
      "Convert unstructured data into insight. Reliably pull figures, quotes and analysis from 250M+ SEC filings, presentations and conference calls.",
    image: cdnUrl("https://hl-public-cdn.s3.us-east-1.amazonaws.com/images/uploads/1787933974637-co-analyst-citation.png?v=2"),
    tag: "In-house AI",
    stat: "10x",
    statLabel: "Faster research",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
      </svg>
    ),
  },
  {
    title: "AI-Powered Search",
    href: "/products#market-intelligence",
    description:
      "Track themes and inflection points in SEC filings and conferences calls with high-precision AI-powered search.",
    image:
      cdnUrl("https://hl-public-cdn.s3.us-east-1.amazonaws.com/images/uploads/1787943491112-hudson-labs-screening.png?v=2"),
    tag: "Proprietary Retrieval",
    stat: "10K+",
    statLabel: "Companies covered",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    ),
  },
  {
    title: "Forensic Risk",
    href: "/products#forensic",
    description:
      "Securities class action prediction, risk monitoring and accounting fraud assessment",
    image:
      cdnUrl("https://hl-public-cdn.s3.us-east-1.amazonaws.com/images/uploads/1787942327962-hudson-labs-forensic-risk.png?v=2"),
    tag: "Quantified Risk",
    stat: "3x",
    statLabel: "Lawsuit Risk",
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
];

export default function Features() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="product" ref={containerRef} className="py-16 sm:py-24 lg:py-32 short:py-8 shorter:py-5 xl:py-48 relative bg-[#0a0a0a] overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:60px_60px]" aria-hidden="true" />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="mb-12 sm:mb-20 lg:mb-20 xl:mb-28">
            <FadeIn>
              <p className="text-xs font-medium tracking-[0.3em] uppercase text-violet-400 mb-5">
                THE CO-ANALYST
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="text-4xl md:text-6xl lg:text-6xl xl:text-7xl font-[family-name:var(--font-display)] tracking-[-0.03em] leading-[1.05] text-white max-w-6xl">
                Better AI for public markets
              </h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-4 text-sm tracking-[0.15em] uppercase text-white/40 font-medium">
                Proprietary AI research, since 2019
              </p>
            </FadeIn>
        </div>

        {/* Feature Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {features.map((feature, i) => (
            <FadeIn key={feature.title} delay={i * 0.15}>
              <TiltCard className="h-full">
                {/* Institutional card treatment: a hairline border and the
                    near-black surface do the separation; a soft dark ambient
                    shadow grounds the card, and a single faint violet glow
                    keeps the brand accent — a whisper, not a neon halo.
                    Mirrors the ProductCards / careers card style. */}
                <Link
                  href={feature.href}
                  className="group relative h-full rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-sm shadow-[0_1px_2px_rgba(0,0,0,0.4),0_16px_40px_-16px_rgba(0,0,0,0.6),0_0_40px_-12px_rgba(139,92,246,0.1)] transition-[border-color,box-shadow] duration-500 hover:border-violet-500/30 hover:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_20px_48px_-16px_rgba(0,0,0,0.7),0_0_50px_-10px_rgba(139,92,246,0.18)] block"
                >
                  {/* Image */}
                  <div className="aspect-[16/10] overflow-hidden relative rounded-t-xl bg-[#0a0a0a]">
                    <Image
                      src={feature.image}
                      alt={feature.title}
                      width={800}
                      height={600}
                      sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 400px"
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
                    {/* Stat overlay */}
                    <div className="absolute bottom-4 left-6">
                      <p className="text-3xl font-[family-name:var(--font-display)] text-white/80">{feature.stat}</p>
                      <p className="text-xs text-white/40 tracking-wider uppercase">{feature.statLabel}</p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 lg:p-8">
                    {/* Tag */}
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-violet-400 bg-violet-500/10 rounded-full border border-violet-500/20 mb-4">
                      {feature.icon}
                      {feature.tag}
                    </span>
                    <h3 className="text-lg font-semibold tracking-tight text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-white/60 leading-relaxed">
                      {feature.description}
                    </p>
                    <div className="mt-5 flex items-center gap-2 text-sm font-medium text-violet-400 group-hover:text-violet-300 transition-colors duration-500">
                      <span>Explore</span>
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </Link>
              </TiltCard>
            </FadeIn>
          ))}
        </div>

        {/* Capabilities + summary */}
        <UniqueCapabilities theme="dark" delay={0.3} className="mt-12 sm:mt-20 lg:mt-20 xl:mt-28" />
        <FadeIn delay={0.4}>
          <p className="mt-12 sm:mt-16 max-w-5xl text-2xl sm:text-3xl lg:text-4xl font-[family-name:var(--font-display)] tracking-[-0.02em] leading-tight text-white/90">
            All results powered by proprietary materiality ranking, topic tagging and high-efficiency retrieval
          </p>
        </FadeIn>
      </div>
    </section>
  );
}
